// src/pages/auth/Register.jsx
import { useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next'; 
import {
  Eye, EyeOff, Mail, Lock, User, Phone, AlertCircle, Loader2,
  CheckCircle, XCircle, MapPin, Globe, Building2, Camera,
  ShoppingBag, Car, Briefcase, Check, ArrowLeft, ArrowRight,
  Star, Zap, Package
} from 'lucide-react';
import { sendOTP } from "../../../services/auth.service";
import { EncryptionUtil } from "../../../utils/encryption.util";

interface CountryCallingCode {
  code: string;
  name: string;
  digits?: number;
  minDigits?: number;
  maxDigits?: number;
}

const COUNTRY_CODES: CountryCallingCode[] = [
  { code: '+212', name: 'Morocco', digits: 9 },
  { code: '+91', name: 'India', digits: 10 },
  { code: '+33', name: 'France', digits: 9 },
  { code: '+34', name: 'Spain', digits: 9 },
  { code: '+1', name: 'USA / Canada', digits: 10 },
  { code: '+44', name: 'UK', digits: 10 },
  { code: '+49', name: 'Germany', minDigits: 10, maxDigits: 11 },
  { code: '+39', name: 'Italy', minDigits: 9, maxDigits: 10 },
  { code: '+971', name: 'UAE', digits: 9 },
  { code: '+966', name: 'Saudi Arabia', digits: 9 },
];

function Field({ label, type = 'text', placeholder, icon: Icon, value, onChange, error, required, extra, maxLength }: {
  label: string; type?: string; placeholder?: string; icon?: any;
  value: string; onChange: (v: string) => void; error?: string;
  required?: boolean; extra?: React.ReactNode; maxLength?: number;
}) {
  return (
    <div>
      <label className="block text-sm text-[#2D323A] mb-1.5">
        {label} {required && <span className="text-[#EF4444]">*</span>}
      </label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />}
        <input
          type={type}
          value={value}
          maxLength={maxLength}
          onChange={(e) => {
            let value = e.target.value;
            if (type === "tel") {
              value = value.replace(/\D/g, "");
            }
            onChange(value);
          }}
          placeholder={placeholder}
          className={`w-full ${Icon ? 'pl-11' : 'pl-4'} ${extra ? 'pr-11' : 'pr-4'} py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors text-sm ${error ? 'border-[#EF4444]' : 'border-[#E5E7EB]'}`}
        />
        {extra}
      </div>
      {error && <p className="flex items-center gap-1 text-[#EF4444] text-xs mt-1"><AlertCircle className="w-3 h-3" /> {error}</p>}
    </div>
  );
}

// ─── Photo Upload ─────────────────────────────────────────────────────────────

function PhotoUpload({preview, onChange, t}: {preview?: string; onChange: (file: File, preview: string) => void; t: any}) {
  const ref = useRef<HTMLInputElement>(null);
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    onChange(file, preview);
  };
  return (
    <div className="flex items-center gap-4">
      <div
        onClick={() => ref.current?.click()}
        className="w-20 h-20 rounded-2xl border-2 border-dashed border-[#E5E7EB] flex items-center justify-center cursor-pointer hover:border-[#1E6DFF] transition-colors overflow-hidden flex-shrink-0 relative group"
      >
        {preview ? (
          <img src={preview} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <Camera className="w-7 h-7 text-[#BFC2C7] group-hover:text-[#1E6DFF] transition-colors" />
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
      <div>
        <p className="text-sm text-[#2D323A]">{t('register.fields.photo')}</p>
        <p className="text-xs text-[#717182]">{t('register.fields.photo_format')}</p>
        <button type="button" onClick={() => ref.current?.click()} className="text-xs text-[#1E6DFF] hover:underline mt-1">
          {preview ? t('register.fields.photo_change') : t('register.fields.photo_upload')}
        </button>
      </div>
    </div>
  );
}

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepBar({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center flex-1 last:flex-none">
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs border-2 transition-all ${i < current ? 'bg-[#1E6DFF] border-[#1E6DFF] text-white' : i === current ? 'border-[#1E6DFF] text-[#1E6DFF] bg-white' : 'border-[#E5E7EB] text-[#717182] bg-white'}`}>
              {i < current ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-xs mt-1 whitespace-nowrap ${i === current ? 'text-[#1E6DFF]' : 'text-[#717182]'}`}>{s}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 mb-4 transition-colors ${i < current ? 'bg-[#1E6DFF]' : 'bg-[#E5E7EB]'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

type AccountType = 'buyer' | 'personal' | 'company';

export function Register({ hideBuyer = false }: { hideBuyer?: boolean }) {
  const { t, i18n } = useTranslation('auth'); 
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDirectCompanySignup = searchParams.get('type') === 'company';
  const [accountType, setAccountType] = useState<AccountType | null>(isDirectCompanySignup ? 'company' : null);
  const [step, setStep] = useState(isDirectCompanySignup ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Shared fields
  const [photo, setPhoto] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [terms, setTerms] = useState(false);

  // Buyer / Personal Seller fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+212');
  const [localPhoneNumber, setLocalPhoneNumber] = useState('');
  const [address, setAddress] = useState('');

  // Company fields
  const [companyName, setCompanyName] = useState('');
  const [regNumber, setRegNumber] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [website, setWebsite] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactCountryCode, setContactCountryCode] = useState('+212');
  const [localContactPhoneNumber, setLocalContactPhoneNumber] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const getSteps = (type: AccountType) => {
    if (type === "buyer") return [t('register.steps.buyer')];
    if (type === "personal") return [t('register.steps.personal')];
    return [t('register.steps.company')];
  };

  const clearErr = (keys: string[]) => setErrors(e => { const n = { ...e }; keys.forEach(k => delete n[k]); return n; });

  const validatePersonal = () => {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = t('register.errors.required');
    if (!lastName.trim()) errs.lastName = t('register.errors.required');
    if (!email.trim()) errs.email = t('register.errors.required');
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = t('register.errors.invalid_email');
    if (!localPhoneNumber.trim()) {errs.phone = t('register.errors.required');} else {
      const selectedCountry = COUNTRY_CODES.find(country => country.code === countryCode);
      if (selectedCountry?.digits && localPhoneNumber.length !== selectedCountry.digits) {
        errs.phone = `Phone number for ${selectedCountry.name} must be ${selectedCountry.digits} digits`;
      } else if (selectedCountry?.minDigits && selectedCountry?.maxDigits && (localPhoneNumber.length < selectedCountry.minDigits || localPhoneNumber.length > selectedCountry.maxDigits)) {
        errs.phone = `Phone number for ${selectedCountry.name} must be between ${selectedCountry.minDigits} and ${selectedCountry.maxDigits} digits`;
      }
    }
    if (!address.trim()) errs.address = t('register.errors.required');
    if (!terms) errs.terms = t('register.errors.terms');
    return errs;
  };

  const validateCompany = () => {
    const errs: Record<string, string> = {};
    if (!companyName.trim()) errs.companyName = t('register.errors.required');
    if (!regNumber.trim()) errs.regNumber = t('register.errors.required');
    if (!companyAddress.trim()) errs.companyAddress = t('register.errors.required');
    if (!contactName.trim()) errs.contactName = t('register.errors.required');
    if (!localContactPhoneNumber.trim()) {errs.contactPhone = t('register.errors.required');} else {
      const selectedCountry = COUNTRY_CODES.find(country => country.code === contactCountryCode);
      if (selectedCountry?.digits && localContactPhoneNumber.length !== selectedCountry.digits) {
        errs.contactPhone = `Phone number for ${selectedCountry.name} must be ${selectedCountry.digits} digits`;
      } else if (selectedCountry?.minDigits && selectedCountry?.maxDigits && (localContactPhoneNumber.length < selectedCountry.minDigits || localContactPhoneNumber.length > selectedCountry.maxDigits)) {
        errs.contactPhone = `Phone number for ${selectedCountry.name} must be between ${selectedCountry.minDigits} and ${selectedCountry.maxDigits} digits`;
      }
    }
    if (!contactEmail.trim()) errs.contactEmail = t('register.errors.required');
    else if (!/\S+@\S+\.\S+/.test(contactEmail)) errs.contactEmail = t('register.errors.invalid_email');
    if (!terms) errs.terms = t('register.errors.terms');
    return errs;
  };

  const handleTypeSelect = (type: AccountType) => {
    setAccountType(type);
    setStep(1);
    setErrors({});
  };

  const handleNext = () => {
    if (!accountType) return;
    const errs = accountType === 'company' ? validateCompany() : validatePersonal();
    const step1Errs = { ...errs };
    delete step1Errs.terms;
    setErrors(step1Errs);
    if (Object.keys(step1Errs).length === 0) setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountType) return;
    const errs = accountType === "company" ? validateCompany() : validatePersonal();
    if (Object.keys(errs).length) {
        setErrors(errs);
        return;
    }
    try {
        setLoading(true);
        const otpEmail = accountType === "company" ? contactEmail : email;
        const nameParts = contactName.trim().split(/\s+/);
        const companyFirstName = nameParts[0] || "";
        const companyLastName = nameParts.slice(1).join(" ") || "";
        const preferredLanguage = (i18n.resolvedLanguage || i18n.language || localStorage.getItem('selectedLanguage') || 'en').split('-')[0];
        await sendOTP(otpEmail, preferredLanguage);
        navigate("/auth/otp", {
          state: {
          purpose: "signup",
          displayEmail: otpEmail,
          formData: {
              accountType,
              firstName: EncryptionUtil.encrypt(accountType === "company" ? companyFirstName : firstName),
              lastName: EncryptionUtil.encrypt(accountType === "company" ? companyLastName : lastName),
              email: EncryptionUtil.encrypt(email),
              phone: EncryptionUtil.encrypt(phone),
              address,
              companyName: companyName ? EncryptionUtil.encrypt(companyName) : "",
              regNumber: regNumber ? EncryptionUtil.encrypt(regNumber) : "",
              website: website ? EncryptionUtil.encrypt(website) : "",
              contactName: contactName ? EncryptionUtil.encrypt(contactName) : "",
              contactPhone: contactPhone ? EncryptionUtil.encrypt(contactPhone) : "",
              contactEmail: contactEmail ? EncryptionUtil.encrypt(contactEmail) : "",
              photo,
              photoFile,
              preferredLanguage: (i18n.resolvedLanguage || i18n.language || localStorage.getItem("selectedLanguage") || "en" ).split("-")[0],
            },
          },
        });
    } catch (error) {
        console.error(error);
    } finally {
        setLoading(false);
    }
  };

  // ── Type Selection Screen ─────────────────────────────────────────────────

  if (!accountType || step === 0) {
    const accountTypes = [
      { id: 'buyer' as AccountType, icon: <ShoppingBag className="w-8 h-8" />, title: t('register.account_types.buyer.title'), desc: t('register.account_types.buyer.description') },
      { id: 'personal' as AccountType, icon: <User className="w-8 h-8" />, title: t('register.account_types.personal.title'), desc: t('register.account_types.personal.description') },
      { id: 'company' as AccountType, icon: <Briefcase className="w-8 h-8" />, title: t('register.account_types.company.title'), desc: t('register.account_types.company.description') },
    ];

    return (
      <div className="w-full max-w-xl">
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-[#E5E7EB]">
          <div className="text-center mb-8">
            <h1 className="text-2xl text-[#2D323A] mb-2">{t('register.title')}</h1>
            <p className="text-[#717182]">{t('register.subtitle')}</p>
          </div>
          <div className="space-y-3">
            {accountTypes
              .filter(type => !hideBuyer || type.id !== "buyer")
              .map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleTypeSelect(t.id)}
                className="w-full flex items-center gap-5 p-5 border-2 border-[#E5E7EB] rounded-2xl hover:border-[#1E6DFF] hover:bg-[#1E6DFF]/5 transition-all group text-left"
              >
                <div className="w-14 h-14 bg-[#F5F7FA] rounded-2xl flex items-center justify-center text-[#717182] group-hover:bg-[#1E6DFF] group-hover:text-white transition-all flex-shrink-0">
                  {t.icon}
                </div>
                <div className="flex-1">
                  <p className="text-[#2D323A] mb-0.5 group-hover:text-[#1E6DFF] transition-colors">{t.title}</p>
                  <p className="text-sm text-[#717182]">{t.desc}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#BFC2C7] group-hover:text-[#1E6DFF] transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>
          <p className="text-center text-sm text-[#717182] mt-6">
            {t('register.already_have')}{' '}
            <Link to="/auth/login" className="text-[#1E6DFF] hover:underline">{t('register.sign_in')}</Link>
          </p>
        </div>
      </div>
    );
  }

  // ── Multi-Step Form ───────────────────────────────────────────────────────

  const steps = getSteps(accountType);
  const currentStepLabel = steps[step - 1] ?? '';
  const isFinalStep = step === 1;

  return (
    <div className="w-full max-w-xl">
      <div className="bg-white rounded-2xl p-8 shadow-xl border border-[#E5E7EB]">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => { if (step === 1) { setAccountType(null); setStep(0); } else setStep(s => s - 1); }}
            className="p-2 hover:bg-[#F5F7FA] rounded-xl text-[#717182] transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 text-center">
            <p className="text-xs text-[#717182] capitalize">{accountType === 'personal' ? 'Personal Seller' : accountType} Account</p>
            <h2 className="text-lg text-[#2D323A]">{currentStepLabel}</h2>
          </div>
          <div className="w-9" />
        </div>

        {steps.length > 1 && <StepBar steps={steps} current={step - 1} />}

        <form onSubmit={handleSubmit}>
          {/* ── STEP 1: Personal Info ── */}
          {(accountType === 'buyer' || accountType === 'personal') && step === 1 && (
            <div className="space-y-4">
              <PhotoUpload 
                preview={photo} 
                onChange={(file, preview) => {setPhotoFile(file); setPhoto(preview);}} 
                t={t}
              />
              <div className="grid grid-cols-2 gap-4">
                <Field 
                  label={t('register.fields.first_name')} 
                  required 
                  value={firstName} 
                  onChange={v => { setFirstName(v); clearErr(['firstName']); }} 
                  error={errors.firstName} 
                  placeholder={t('register.fields.first_name_placeholder')} 
                  icon={User} 
                />
                <Field 
                  label={t('register.fields.last_name')} 
                  required 
                  value={lastName} 
                  onChange={v => { setLastName(v); clearErr(['lastName']); }} 
                  error={errors.lastName} 
                  placeholder={t('register.fields.last_name_placeholder')} 
                />
              </div>
              <Field 
                label={t('register.fields.email')} 
                required 
                type="email" 
                value={email} 
                onChange={v => { setEmail(v); clearErr(['email']); }} 
                error={errors.email} 
                placeholder={t('register.fields.email_placeholder')} 
                icon={Mail} 
              />
              {/* <Field 
                label={t('register.fields.phone')} 
                type="tel" 
                required 
                value={phone} 
                maxLength={10} 
                onChange={v => { setPhone(v); clearErr(['phone']); }} 
                error={errors.phone} 
                placeholder={t('register.fields.phone_placeholder')} 
                icon={Phone} 
              /> */}
              <div>
                <label className="block text-sm text-[#2D323A] mb-1.5">{t('register.fields.phone')}<span className="text-[#EF4444]"> *</span></label>
                <div className={`flex w-full bg-[#F5F7FA] border rounded-xl overflow-hidden focus-within:border-[#1E6DFF] transition-colors ${errors.phone ? 'border-[#EF4444]' : 'border-[#E5E7EB]'}`}>
                  {/* Country Code */}
                  <select
                    value={countryCode}
                    onChange={(e) => {
                      const nextCountryCode = e.target.value;
                      setCountryCode(nextCountryCode);
                      const selectedCountry = COUNTRY_CODES.find(country => country.code === nextCountryCode);
                      const maxLength = selectedCountry?.digits || selectedCountry?.maxDigits || 15;
                      const trimmedPhone = localPhoneNumber.slice(0, maxLength);
                      setLocalPhoneNumber(trimmedPhone);
                      setPhone(trimmedPhone ? `${nextCountryCode}${trimmedPhone}` : '');
                      clearErr(['phone']);
                    }}
                    className="px-3 py-3 bg-transparent border-r border-[#E5E7EB] text-sm text-[#2D323A] focus:outline-none"
                  >
                    {COUNTRY_CODES.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.name} ({country.code})
                      </option>
                    ))}
                  </select>
                  {/* Phone Number */}
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                    <input
                      type="tel"
                      value={localPhoneNumber}
                      maxLength={
                        COUNTRY_CODES.find(country => country.code === countryCode)?.digits ||
                        COUNTRY_CODES.find(country => country.code === countryCode)?.maxDigits || 15
                      }
                      onChange={(e) => {
                        const selectedCountry = COUNTRY_CODES.find(country => country.code === countryCode);
                        const maxLength = selectedCountry?.digits || selectedCountry?.maxDigits || 15;
                        const digits = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, maxLength);
                        setLocalPhoneNumber(digits);
                        setPhone(digits ? `${countryCode}${digits}` : '');
                        clearErr(['phone']);
                      }}
                      placeholder={t('register.fields.phone_placeholder')}
                      className="w-full pl-11 pr-4 py-3 bg-transparent focus:outline-none text-sm"
                    />
                  </div>
                </div>

                {errors.phone && (
                  <p className="flex items-center gap-1 text-[#EF4444] text-xs mt-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.phone}
                  </p>
                )}
              </div>
              <Field 
                label={t('register.fields.address')} 
                required 
                value={address} 
                onChange={v => { setAddress(v); clearErr(['address']); }} 
                error={errors.address} 
                placeholder={t('register.fields.address_placeholder')} 
                icon={MapPin} 
              />
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={terms} onChange={e => { setTerms(e.target.checked); clearErr(['terms']); }} className="w-4 h-4 mt-0.5 accent-[#1E6DFF]" />
                <span className="text-sm text-[#717182]">
                  {t('register.fields.terms')}{' '}
                  <Link to="/terms" className="text-[#1E6DFF] hover:underline">Terms</Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-[#1E6DFF] hover:underline">Privacy Policy</Link>
                </span>
              </label>
              {errors.terms && <p className="flex items-center gap-1 text-[#EF4444] text-xs"><AlertCircle className="w-3 h-3" /> {errors.terms}</p>}
            </div>
          )}

          {/* ── STEP 1: Company Info ── */}
          {accountType === 'company' && step === 1 && (
            <div className="space-y-4">
              <div className="p-4 bg-[#F5F7FA] rounded-xl border border-[#E5E7EB]">
                <p className="text-xs text-[#717182] mb-3 flex items-center gap-1.5">
                  <Building2 className="w-4 h-4" /> Company Details
                </p>
                <div className="space-y-4">
                  <Field 
                    label={t('register.fields.company_name')} 
                    required 
                    value={companyName} 
                    onChange={v => { setCompanyName(v); clearErr(['companyName']); }} 
                    error={errors.companyName} 
                    placeholder={t('register.fields.company_name_placeholder')} 
                    icon={Building2} 
                  />
                  <Field 
                    label={t('register.fields.reg_number')} 
                    required 
                    value={regNumber} 
                    onChange={v => { setRegNumber(v); clearErr(['regNumber']); }} 
                    error={errors.regNumber} 
                    placeholder={t('register.fields.reg_number_placeholder')} 
                  />
                  <Field 
                    label={t('register.fields.company_address')} 
                    required 
                    value={companyAddress} 
                    onChange={v => { setCompanyAddress(v); clearErr(['companyAddress']); }} 
                    error={errors.companyAddress} 
                    placeholder={t('register.fields.company_address_placeholder')} 
                    icon={MapPin} 
                  />
                  <Field 
                    label={t('register.fields.website')} 
                    value={website} 
                    onChange={setWebsite} 
                    placeholder={t('register.fields.website_placeholder')} 
                    icon={Globe} 
                  />
                </div>
              </div>
              <div className="p-4 bg-[#F5F7FA] rounded-xl border border-[#E5E7EB]">
                <p className="text-xs text-[#717182] mb-3 flex items-center gap-1.5">
                  <User className="w-4 h-4" /> Contact Person
                </p>
                <div className="space-y-4">
                  <Field 
                    label={t('register.fields.contact_name')} 
                    required 
                    value={contactName} 
                    onChange={v => { setContactName(v); clearErr(['contactName']); }} 
                    error={errors.contactName} 
                    placeholder={t('register.fields.contact_name_placeholder')} 
                    icon={User} 
                  />
                  {/* <Field 
                    label={t('register.fields.contact_phone')} 
                    type="tel" 
                    maxLength={10} 
                    required 
                    value={contactPhone} 
                    onChange={v => { setContactPhone(v); clearErr(['contactPhone']); }} 
                    error={errors.contactPhone} 
                    placeholder={t('register.fields.contact_phone_placeholder')} 
                    icon={Phone} 
                  /> */}
                  <div>
                    <label className="block text-sm text-[#2D323A] mb-1.5">
                      {t('register.fields.contact_phone')}
                      <span className="text-[#EF4444]"> *</span>
                    </label>

                    <div
                      className={`flex w-full bg-white border rounded-xl overflow-hidden focus-within:border-[#1E6DFF] transition-colors ${
                        errors.contactPhone
                          ? 'border-[#EF4444]'
                          : 'border-[#E5E7EB]'
                      }`}
                    >
                      {/* Country Code */}
                      <select
                        value={contactCountryCode}
                        onChange={(e) => {
                          const nextCountryCode = e.target.value;

                          setContactCountryCode(nextCountryCode);

                          const selectedCountry = COUNTRY_CODES.find(
                            country => country.code === nextCountryCode
                          );

                          const maxLength =
                            selectedCountry?.digits ||
                            selectedCountry?.maxDigits ||
                            15;

                          const trimmedPhone =
                            localContactPhoneNumber.slice(0, maxLength);

                          setLocalContactPhoneNumber(trimmedPhone);

                          setContactPhone(
                            trimmedPhone
                              ? `${nextCountryCode}${trimmedPhone}`
                              : ''
                          );

                          clearErr(['contactPhone']);
                        }}
                        className="px-3 py-3 bg-transparent border-r border-[#E5E7EB] text-sm text-[#2D323A] focus:outline-none"
                      >
                        {COUNTRY_CODES.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name} ({country.code})
                          </option>
                        ))}
                      </select>

                      {/* Contact Phone */}
                      <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />

                        <input
                          type="tel"
                          value={localContactPhoneNumber}
                          maxLength={
                            COUNTRY_CODES.find(
                              country => country.code === contactCountryCode
                            )?.digits ||
                            COUNTRY_CODES.find(
                              country => country.code === contactCountryCode
                            )?.maxDigits ||
                            15
                          }
                          onChange={(e) => {
                            const selectedCountry = COUNTRY_CODES.find(
                              country => country.code === contactCountryCode
                            );

                            const maxLength =
                              selectedCountry?.digits ||
                              selectedCountry?.maxDigits ||
                              15;

                            const digits = e.target.value
                              .replace(/\D/g, '')
                              .slice(0, maxLength);

                            setLocalContactPhoneNumber(digits);

                            setContactPhone(
                              digits
                                ? `${contactCountryCode}${digits}`
                                : ''
                            );

                            clearErr(['contactPhone']);
                          }}
                          placeholder={t('register.fields.contact_phone_placeholder')}
                          className="w-full pl-11 pr-4 py-3 bg-transparent focus:outline-none text-sm"
                        />
                      </div>
                    </div>

                    {errors.contactPhone && (
                      <p className="flex items-center gap-1 text-[#EF4444] text-xs mt-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.contactPhone}
                      </p>
                    )}
                  </div>
                  <Field 
                    label={t('register.fields.contact_email')} 
                    required 
                    type="email" 
                    value={contactEmail} 
                    onChange={v => { setContactEmail(v); clearErr(['contactEmail']); }} 
                    error={errors.contactEmail} 
                    placeholder={t('register.fields.contact_email_placeholder')} 
                    icon={Mail} 
                  />
                </div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={terms} onChange={e => { setTerms(e.target.checked); clearErr(['terms']); }} className="w-4 h-4 mt-0.5 accent-[#1E6DFF]" />
                <span className="text-sm text-[#717182]">
                  {t('register.fields.terms')}{' '}
                  <Link to="/terms" className="text-[#1E6DFF] hover:underline">Terms</Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-[#1E6DFF] hover:underline">Privacy Policy</Link>
                </span>
              </label>
              {errors.terms && <p className="flex items-center gap-1 text-[#EF4444] text-xs"><AlertCircle className="w-3 h-3" /> {errors.terms}</p>}
            </div>
          )}

          {/* ── Action Button ── */}
          <div className="mt-6">
            {isFinalStep ? (
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {t('register.buttons.creating')}</> : t('register.buttons.create_account')}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2"
              >
                {t('register.buttons.continue')} <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </form>

        <p className="text-center text-sm text-[#717182] mt-6">
          {t('register.already_have')}{' '}
          <Link to="/auth/login" className="text-[#1E6DFF] hover:underline">{t('register.sign_in')}</Link>
        </p>
      </div>
    </div>
  );
}
